import React from "react";
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";

// A select over the user's projects (see hooks/useProjects). Controlled: the
// value is a project id or "" for none, and `onChange` gets the new id.
//
// `required` marks the field and, with `error`, says so under it; the caller
// owns the actual rule (what may be saved without a project). `disabled` plus
// `helperText` is how a caller shows a link that can no longer be changed.
// `allowNone` adds a "None" option (value "") so an optional link can be
// cleared again once set; `emptyText` replaces the empty-list message.
export default function ProjectPicker({
    projects = [],
    value,
    onChange,
    loading = false,
    disabled = false,
    required = false,
    allowNone = false,
    emptyText,
    error = false,
    helperText,
    label,
    id = "project-picker",
    size = "small",
    fullWidth = false,
    sx,
}) {
    const intl = useIntl();
    const text =
        label ||
        intl.formatMessage({ id: "project_picker.label", defaultMessage: "Project" });
    const current = value == null ? "" : String(value);
    // Keep a saved project selectable even before (or without) the list loading
    // it — otherwise MUI warns and the field reads blank.
    const known = projects.some((p) => String(p.id) === current);

    return (
        <FormControl
            size={size}
            fullWidth={fullWidth}
            required={required}
            error={error}
            disabled={disabled || loading}
            sx={{ minWidth: 220, ...sx }}
        >
            <InputLabel id={`${id}-label`}>{text}</InputLabel>
            <Select
                labelId={`${id}-label`}
                value={current}
                label={text}
                onChange={(e) => onChange(e.target.value === "" ? "" : String(e.target.value))}
                inputProps={{ "aria-label": text }}
            >
                {current && !known && (
                    <MenuItem value={current}>#{current}</MenuItem>
                )}
                {allowNone && (
                    <MenuItem value="">
                        <em>
                            <FormattedMessage id="project_picker.none" defaultMessage="None" />
                        </em>
                    </MenuItem>
                )}
                {projects.length === 0 ? (
                    // With "None" holding the "" value, the notice takes its own.
                    <MenuItem value={allowNone ? "__no_projects" : ""} disabled>
                        {emptyText || (
                            <FormattedMessage
                                id="documents.no_projects_available"
                                defaultMessage="No projects available"
                            />
                        )}
                    </MenuItem>
                ) : (
                    projects.map((project) => (
                        <MenuItem key={project.id} value={String(project.id)}>
                            {project.name || `#${project.id}`}
                        </MenuItem>
                    ))
                )}
            </Select>
            {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
        </FormControl>
    );
}
